import { QA_VALIDATION_STATUS } from "./validationConstants";

// What the recommendation form DOES with a QA/QC verdict, as a pure function.
//
// Lifted out of Form.jsx — which is 8,000 lines of class component and cannot be driven from a test
// without standing up most of the app — for one reason: the property this encodes is exactly the kind
// that breaks invisibly. Every branch here decides whether a recommendation may be saved, and the failure
// mode of getting it wrong is a save that goes through looking checked when nothing checked it. That is
// not something to leave resting on a code review.
//
// Follows the same split the payload builder already uses (`buildDraftPayload.js`): the form keeps the
// state and the modal, this keeps the judgement.

export const QA_OUTCOME = {
    CLEAN: "clean", // nothing to report; the save proceeds on its own
    FINDINGS: "findings", // a rule reached a verdict and disagreed; the surveyor must look
    INCOMPLETE: "incomplete" // Otto did not answer; the surveyor is told and decides
};

// The sentence shown when the failure carries none of its own — a transport error, or a response that
// never arrived. The backend writes a better one per rule whenever it got far enough to have an opinion.
export const GENERIC_FAILURE_MESSAGE = "Otto could not complete validation of this recommendation.";

/**
 * @param verdict  the /qa_qc/check_draft body, or null when the call did not return one
 * @param refusal  a {message} for a 403 — the endpoint declining this account outright
 */
export const decideQaOutcome = ({ verdict, refusal = null } = {}) => {
    const findings = (verdict && verdict.findings) || [];
    const failed = (verdict && verdict.failed) || [];
    // WHAT ACTUALLY RAN. Carried through every branch because "no findings" means two different things —
    // "both rules compared and agreed" and "neither rule had anything to look at" — and until these were
    // surfaced both rendered as the same green tick. A surveyor whose narrative Otto could not read was
    // told "Looks good" with no way to learn it had never been read.
    const checked = (verdict && verdict.checked_rules) || [];
    const skipped = (verdict && verdict.skipped) || [];

    // Findings win over an incomplete run. `status: "partial"` means one rule answered and another did
    // not — but the one that answered reached a real verdict, and that verdict is a finding.
    if (findings.length) {
        return {
            outcome: QA_OUTCOME.FINDINGS,
            status: QA_VALIDATION_STATUS.FAILED,
            findings,
            failed,
            checked,
            skipped,
            proceed: false
        };
    }

    // NO FINDINGS IS NOT THE SAME AS NOTHING WRONG, and this branch is the whole reason this function is
    // testable. An empty `findings` from a run that could not complete — an outage, a timeout, or the
    // endpoint refusing this account — must never read as a clean bill of health. It is surfaced, and the
    // surveyor decides; what it must never do is quietly let the save through as though Otto had looked.
    if (!verdict || verdict.status === "unavailable" || failed.length || refusal) {
        return {
            outcome: QA_OUTCOME.INCOMPLETE,
            status: QA_VALIDATION_STATUS.INCOMPLETE,
            findings: [],
            // The backend's own per-rule messages when it produced any; otherwise the refusal's, which is
            // more specific than the generic one; otherwise the generic one.
            failed: failed.length ? failed : [refusal || { message: GENERIC_FAILURE_MESSAGE }],
            checked,
            skipped,
            proceed: false
        };
    }

    return {
        outcome: QA_OUTCOME.CLEAN,
        status: QA_VALIDATION_STATUS.PASSED,
        findings: [],
        failed: [],
        checked,
        skipped,
        proceed: true
    };
};

// WHY THE FORM DID NOT EVEN ASK OTTO. `isWorthChecking` short-circuits two cases before any request, and
// until now both were a silent pass with no modal at all — the surveyor saw a save go through and had no
// way to know that nothing had been validated. The backend reports its own skips per rule; these are the
// two it never hears about, so the sentences are written here.
//
// They are NOT a mirror of the backend's table: these describe a decision this file made, in facts the
// surveyor can check on the screen in front of them, which is also why they can be held for the shorter
// beat — "there is no narrative" is not a surprise to whoever left it blank.
export const localSkipReasons = (payload = {}) => {
    const has = value => value !== null && value !== undefined && value !== "";
    if (!payload.note_html || !payload.note_html.trim()) {
        return [
            {
                rule_key: "local_no_narrative",
                short_title: "Narrative",
                message: "There is no narrative, so there was nothing for Otto to compare the recorded values against."
            }
        ];
    }
    if (!has(payload.installed_year) && !has(payload.asset_installed_year) && !has(payload.condition) && !has(payload.asset_condition)) {
        return [
            {
                rule_key: "local_nothing_recorded",
                short_title: "Recorded values",
                message:
                    "No Installed Year and no Condition are recorded on this recommendation, so there was nothing for the narrative to contradict."
            }
        ];
    }
    return [];
};

/** Did anything actually get compared? A clean result with nothing checked is not a clean bill of health.
 *
 * The save still proceeds — Otto's reach must not decide whether a surveyor can record their work — but
 * what the screen SAYS has to differ, which is the whole point of separating these two cases. */
export const nothingWasChecked = ({ checked = [], skipped = [] } = {}) => !checked.length && skipped.length > 0;
